import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { BREAKPOINTS } from "../../styles/breakpoints";

/**
 * 404 page.
 *
 * The router previously had no `path="*"` route at all, so any unmatched URL
 * -- a typo, a stale bookmark, an old link in an email -- rendered an empty
 * <RoutesDOM> and the user got a blank white page with no message and no way
 * back except editing the address bar.
 *
 * Deliberately does NOT render the Sidebar: an unknown route may be hit while
 * logged out, and mounting the nav shell would fire the authenticated data
 * queries for a page that does not exist.
 */
const useStyles = createUseStyles({
  root: {
    minHeight: "var(--app-vh)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    padding: "40px 24px",
    textAlign: "center",
    backgroundColor: colors.canvas,
    [BREAKPOINTS.down.sm]: {
      padding: "32px 16px",
      gap: "16px",
    },
  },
  code: {
    fontSize: "72px",
    fontWeight: 800,
    lineHeight: 1,
    color: colors.primary,
    letterSpacing: "-0.02em",
    [BREAKPOINTS.down.sm]: {
      fontSize: "56px",
    },
  },
  title: {
    fontSize: "22px",
    fontWeight: 600,
    color: colors.primaryText,
    margin: 0,
    [BREAKPOINTS.down.sm]: {
      fontSize: "18px",
    },
  },
  message: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: colors.secondaryTextColor,
    margin: 0,
    maxWidth: "420px",
  },
  /* The attempted path helps a user report the broken link accurately.
     `break-all` because a long URL would otherwise widen the page. */
  path: {
    fontSize: "12px",
    fontFamily: "source-code-pro, Menlo, Monaco, Consolas, monospace",
    color: colors.secondaryTextColor,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: "8px",
    padding: "6px 10px",
    maxWidth: "100%",
    overflowWrap: "anywhere",
    wordBreak: "break-all",
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: "4px",
  },
  button: {
    appearance: "none",
    border: `1px solid ${colors.borderLight}`,
    borderRadius: "10px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    backgroundColor: colors.surface,
    color: colors.primaryText,
    transition: "border-color 150ms ease, background-color 150ms ease",
    "&:hover": {
      borderColor: colors.primary,
      backgroundColor: colors.surfaceHover,
    },
    "&:focus-visible": {
      outline: `2px solid ${colors.primary}`,
      outlineOffset: "2px",
    },
  },
  buttonPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: "#fff",
    "&:hover": {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      filter: "brightness(1.08)",
    },
  },
});

export const NotFound = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isAuthenticated = !!localStorage.getItem("token");

  return (
    <div className={classes.root}>
      <div className={classes.code}>404</div>

      <h1 className={classes.title}>
        {t("notFound.title", "Page not found")}
      </h1>

      <p className={classes.message}>
        {t(
          "notFound.message",
          "This page does not exist, or it may have been moved. Check the address, or head back."
        )}
      </p>

      <span className={classes.path}>{pathname}</span>

      <div className={classes.actions}>
        <button
          type="button"
          className={classes.button}
          onClick={() => navigate(-1)}
        >
          {t("notFound.goBack", "Go back")}
        </button>
        <button
          type="button"
          className={`${classes.button} ${classes.buttonPrimary}`}
          onClick={() => navigate(isAuthenticated ? "/dashboard" : "/")}
        >
          {isAuthenticated
            ? t("notFound.goDashboard", "Go to dashboard")
            : t("notFound.goLogin", "Go to sign in")}
        </button>
      </div>
    </div>
  );
};

export default NotFound;
